import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Divider,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SplitButton,
  tokens,
  TagPicker,
  TagPickerControl,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
} from "@fluentui/react-components";
import { TextEffectsSparkle20Regular } from "@fluentui/react-icons";
import { useGetFlattenedStylesQuery } from "src/api/server-api";
import { NO_CHARACTER_STYLE, NO_CHARACTER_STYLE_DISPLAY } from "src/services";
import "./SelectedParagraphControls.css";

import type { SelectedParagraphStyleTileProps } from "./SelectedParagraphStyleTile";
import type { MenuButtonProps, TagPickerOnOptionSelectData } from "@fluentui/react-components";
import type { OutlineMode, StyleDefinition } from "src/types";

// Below this width the Restyle button shows its icon alone, so the style picker
// keeps a usable amount of room in a narrow task pane.
const NARROW_CONTAINER = "@container (max-width: 260px)";

const useStyles = makeStyles({
  divider: { minHeight: "20px" },
  restyleButton: {
    minWidth: "auto",
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    // Sized like Fluent's icon-only button.
    [NARROW_CONTAINER]: {
      minWidth: "32px",
      maxWidth: "32px",
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  // The hidden label still counts as content to Fluent, which keeps the icon's
  // label gap and pushes it off-centre.
  restyleIcon: {
    [NARROW_CONTAINER]: { marginRight: 0 },
  },
  restyleLabel: {
    [NARROW_CONTAINER]: { display: "none" },
  },
});

export interface SelectedParagraphControlsProps extends Pick<
  SelectedParagraphStyleTileProps,
  "notInProfile" | "noSelectedCharacterStyle"
> {
  isMultiSelect?: boolean;
  loading?: boolean;
  outlineMode?: OutlineMode;
  /** Style the review is filtered to; "Restyle all" is only offered when set. */
  restyleAllStyle?: string | null;
  selectedStyle?: string | null;
  totalTracked?: number;
  tracked?: number;
  onApprove?: () => Promise<void>;
  onApproveAll?: () => Promise<void>;
  onRestyle?: (newStyle: string) => Promise<void>;
  onRestyleAll?: (newStyle: string) => Promise<void>;
}

export function SelectedParagraphControls(props: SelectedParagraphControlsProps) {
  const { data } = useGetFlattenedStylesQuery("rhml");
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const [pendingStyle, setPendingStyle] = useState<string | null>(null);

  useEffect(() => {
    setQuery("");
    setPendingStyle(null);
  }, [props.selectedStyle]);

  const noTrackedChanges = (props.tracked ?? 0) === 0;
  const noTotalTrackedChanges = (props.totalTracked ?? 0) === 0;
  const styleActionsDisabled =
    props.loading || props.notInProfile || props.noSelectedCharacterStyle;

  const isCharacterMode = props.outlineMode === "character";

  const matchingStyles: [string, string][] = useMemo(() => {
    const noCharEntry: [string, string] = [NO_CHARACTER_STYLE, NO_CHARACTER_STYLE_DISPLAY];
    if (!data) return isCharacterMode ? [noCharEntry] : [];

    const styleDefinitionMap: Record<string, StyleDefinition> = {};
    for (const styleDefinition of data) {
      styleDefinitionMap[styleDefinition.style] = styleDefinition;
    }

    const queryLower = query.toLowerCase();

    const styleDefinitionEntries = Object.entries(styleDefinitionMap).filter(([k, v]) => {
      if (isCharacterMode) {
        return v.style_type === "char";
      }

      if (props.outlineMode === "chapter" || props.outlineMode === "style") {
        return v.style_type === "parag";
      }

      return true;
    });

    const entries = styleDefinitionEntries.map<[string, string]>(([k, v]) => [
      v.style,
      v.full_name,
    ]);

    if (!query) {
      if (isCharacterMode) {
        return [noCharEntry, ...entries.slice(0, 4)];
      }
      return entries.slice(0, 5);
    }

    const scored = entries
      .map<[string, string, number]>(([k, v]) => {
        const kLower = k.toLowerCase();
        const vLower = v.toLowerCase();

        let score = 0;

        if (kLower.includes(queryLower) || vLower.includes(queryLower)) {
          score = 1;
        }
        if (kLower.startsWith(queryLower) || vLower.startsWith(queryLower)) {
          score = 2;
        }
        if (kLower === queryLower || vLower === queryLower) {
          score = 3;
        }

        return [k, v, score];
      })
      .sort((a, b) => b[2] - a[2])
      .slice(0, 5)
      .map<[string, string]>(([k, v]) => [k, v]);

    if (isCharacterMode) {
      const displayLower = NO_CHARACTER_STYLE_DISPLAY.toLowerCase();
      if (displayLower.includes(queryLower)) {
        return [noCharEntry, ...scored.slice(0, 4)];
      }
    }

    return scored;
  }, [data, query, props.outlineMode, isCharacterMode]);

  const handleOptionSelect = (
    _: React.SyntheticEvent | Event,
    data: TagPickerOnOptionSelectData
  ) => {
    if (data.value === NO_CHARACTER_STYLE) {
      setQuery(NO_CHARACTER_STYLE_DISPLAY);
    } else {
      const style = matchingStyles.find(([k]) => k.toLowerCase() === data.value.toLowerCase());
      if (style) {
        setQuery(`[${style[0]}] ${style[1]}`);
      }
    }
    if (data.value.toLowerCase() !== props.selectedStyle?.toLocaleLowerCase()) {
      setPendingStyle(data.value);
    } else {
      setPendingStyle(null);
    }
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setPendingStyle(null);
  }, []);

  const handleRestyle = async () => {
    if (pendingStyle && props.onRestyle) {
      await props.onRestyle(pendingStyle);
      setQuery("");
      setPendingStyle(null);
    }
  };

  const handleRestyleAll = async () => {
    if (pendingStyle && props.onRestyleAll) {
      await props.onRestyleAll(pendingStyle);
      setQuery("");
      setPendingStyle(null);
    }
  };

  const restyleDisabled = props.loading || !pendingStyle;
  const restyleAllDisabled = restyleDisabled || !props.restyleAllStyle || !props.onRestyleAll;

  return (
    <div className="selected-paragraph-controls">
      <div className="selected-paragraph-controls-accept">
        <Button
          disabled={styleActionsDisabled || noTotalTrackedChanges}
          onClick={props.onApproveAll}
        >
          Accept all
        </Button>
        <Button
          appearance={
            props.notInProfile || props.noSelectedCharacterStyle ? "secondary" : "primary"
          }
          disabled={styleActionsDisabled || noTrackedChanges}
          onClick={props.onApprove}
        >
          Accept {props.isMultiSelect ? "selection" : "style"}
        </Button>
      </div>
      <Divider className={styles.divider}>or</Divider>
      <div className="selected-paragraph-controls-restyle">
        <TagPicker disabled={props.loading} onOptionSelect={handleOptionSelect}>
          <TagPickerControl autoCorrect="off" className="selected-paragraph-controls-tagpicker">
            <TagPickerInput
              autoCorrect="off"
              placeholder="Type a style name..."
              value={query}
              onChange={handleChange}
            />
          </TagPickerControl>
          <TagPickerList>
            {matchingStyles.map(([style, fullName]) => {
              const label = style === NO_CHARACTER_STYLE ? fullName : `[${style}] ${fullName}`;
              return (
                <TagPickerOption key={style} text={label} value={style}>
                  {label}
                </TagPickerOption>
              );
            })}
          </TagPickerList>
        </TagPicker>
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            {(triggerProps: MenuButtonProps) => (
              <SplitButton
                appearance={
                  props.notInProfile || props.noSelectedCharacterStyle ? "primary" : "secondary"
                }
                className="selected-paragraph-controls-restyle-button"
                disabled={restyleDisabled}
                icon={{ children: <TextEffectsSparkle20Regular />, className: styles.restyleIcon }}
                menuButton={{
                  ...triggerProps,
                  "aria-label": "More restyle options",
                  disabled: restyleAllDisabled,
                }}
                primaryActionButton={{
                  "aria-label": props.isMultiSelect ? "Restyle selection" : "Restyle",
                  className: styles.restyleButton,
                  onClick: handleRestyle,
                }}
              >
                <span className={styles.restyleLabel}>
                  Restyle{props.isMultiSelect && " selection"}
                </span>
              </SplitButton>
            )}
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem disabled={restyleAllDisabled} onClick={handleRestyleAll}>
                {pendingStyle === NO_CHARACTER_STYLE
                  ? `Restyle all [${NO_CHARACTER_STYLE_DISPLAY}]`
                  : `Restyle all [${pendingStyle}]`}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </div>
  );
}
